import { z } from 'zod';

// Client-side environment schema
const clientEnvSchema = z.object({
  VITE_SUPABASE_URL: z.string().url('Invalid Supabase URL'),
  VITE_SUPABASE_ANON_KEY: z.string().min(1, 'Supabase anon key is required'),
  VITE_SERVER_URL: z.string().url('Invalid server URL').optional(),
  VITE_IP_ENDPOINT: z.string().optional(),
  PLATFORM_COMMISSION_RATE: z.string().optional(),
});

// Server-side environment schema
const serverEnvSchema = z.object({
  SUPABASE_URL: z.string().url('Invalid Supabase URL'),
  SUPABASE_SERVICE_ROLE_KEY: z.string().min(1, 'Supabase service role key is required'),
  STRIPE_API_KEY: z.string().min(1, 'Stripe API key is required'),
  STRIPE_WEBHOOK_SECRET: z.string().min(1, 'Stripe webhook secret is required'),
  SHIPPO_API_KEY: z.string().min(1, 'Shippo API key is required'),
  NODE_ENV: z.enum(['development', 'production', 'test']).optional(),
});

/**
 * Validate client-side environment variables
 */
export const validateClientEnv = () => {
  if (typeof window === 'undefined') return; // Skip on server
  
  try {
    const env = {
      VITE_SUPABASE_URL: import.meta.env?.VITE_SUPABASE_URL,
      VITE_SUPABASE_ANON_KEY: import.meta.env?.VITE_SUPABASE_ANON_KEY,
      VITE_SERVER_URL: import.meta.env?.VITE_SERVER_URL,
      VITE_IP_ENDPOINT: import.meta.env?.VITE_IP_ENDPOINT,
      PLATFORM_COMMISSION_RATE: import.meta.env?.PLATFORM_COMMISSION_RATE,
    };

    clientEnvSchema.parse(env);
    // Environment validated - logging only in development
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join('\n');
      console.error('❌ Client environment validation failed:\n', errors);
      
      // In production, we want to fail fast
      if (import.meta.env?.PROD) {
        throw new Error(`Environment validation failed:\n${errors}`);
      }
    }
    throw error;
  }
};

/**
 * Validate server-side environment variables
 */
export const validateServerEnv = (env: Record<string, string | undefined> = process.env) => {
  try {
    serverEnvSchema.parse(env);
    return true;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join('\n');
      console.error('❌ Server environment validation failed:\n', errors);
      
      // In production, we want to fail fast
      if (env.NODE_ENV === 'production') {
        throw new Error(`Environment validation failed:\n${errors}`);
      }
      
      return false;
    }
    throw error;
  }
};

/**
 * Get environment info for debugging
 */
export const getEnvInfo = () => {
  const isClient = typeof window !== 'undefined';
  const isDev = isClient ? import.meta.env?.DEV : process.env.NODE_ENV !== 'production';
  
  return {
    isClient,
    isDevelopment: isDev,
    nodeEnv: isClient ? 'browser' : process.env.NODE_ENV,
    hasRequiredClientEnv: !!(import.meta.env?.VITE_SUPABASE_URL && import.meta.env?.VITE_SUPABASE_ANON_KEY),
    hasRequiredServerEnv: !isClient && !!(process.env?.SUPABASE_URL && process.env?.SUPABASE_SERVICE_ROLE_KEY),
  };
};

// Auto-validate on import in appropriate environments
if (typeof window !== 'undefined') {
  // Client-side validation
  try {
    validateClientEnv();
  } catch (error) {
    // Don't crash the app in development, just log
    if (import.meta.env?.DEV) {
      console.warn('Environment validation failed in development mode:', error);
    } else {
      throw error;
    }
  }
}
